import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Card from "../components/common/Card";
import DataTable from "../components/common/DataTable";
import Button from "../components/common/Button";
import EmptyState from "../components/common/EmptyState";
import Modal from "../components/common/Modal";
import { listPatients, deletePatient } from "../services/patientService";
import { listPatientDocuments } from "../services/documentService";
import { getPendingRequestsCount } from "../services/accessRequestService";
import { useAuth } from "../contexts/AuthContext";
import { formatDate } from "../utils/format";
import { useToast } from "../contexts/ToastContext";

export default function PatientsPage() {
  const { profile, user } = useAuth();
  const { push } = useToast();
  const isAdmin = profile?.role === "admin";
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [pendingCount, setPendingCount] = useState(0);
  const [deleting, setDeleting] = useState(null);
  const [docCounts, setDocCounts] = useState({});

  const load = useCallback(async () => {
    const all = await listPatients(100, { uid: user?.uid, role: profile?.role });
    setPatients(all);
    const counts = {};
    await Promise.all(
      all.map(async (p) => {
        const docs = await listPatientDocuments(p.id);
        counts[p.id] = docs.length;
      })
    );
    setDocCounts(counts);
    if (isAdmin) {
      const pending = await getPendingRequestsCount();
      setPendingCount(pending);
    }
  }, [isAdmin, profile?.role, user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () => patients.filter((p) => `${p.firstName} ${p.lastName}`.toLowerCase().includes(search.toLowerCase())),
    [patients, search]
  );

  const columns = [
    {
      key: "name",
      label: "Patient Name",
      render: (p) => (
        <Link to={`/patients/${p.id}`} style={{ fontWeight: 600 }}>
          {p.firstName} {p.lastName}
        </Link>
      ),
    },
    { key: "dateOfBirth", label: "Date Of Birth" },
    { key: "createdByName", label: "Created By" },
    { key: "createdAt", label: "Created Date", render: (p) => formatDate(p.createdAt) },
    {
      key: "documents",
      label: "Documents",
      render: (p) =>
        isAdmin ? (
          <button type="button" className="btn btn-secondary" onClick={() => navigate(`/patients/${p.id}#documents`)}>
            {docCounts[p.id] || 0}
          </button>
        ) : (
          docCounts[p.id] || 0
        ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (p) => (
        <div className="inline-actions">
          <Link to={`/patients/${p.id}`}>View</Link>
          {isAdmin ? (
            <Button variant="danger" onClick={() => setDeleting(p)}>
              Delete
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <AppShell
      title="Patients"
      pendingCount={pendingCount}
      rightTopContent={
        <div className="control-row">
          <input className="control-input" placeholder="Search by name..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button onClick={() => navigate("/patients/new")}>+ New Patient</Button>
        </div>
      }
    >
      <Card>
        <DataTable
          columns={columns}
          rows={filtered}
          empty={<EmptyState title="No patients yet" description="Create your first patient" ctaLabel="Create Patient" onCta={() => navigate("/patients/new")} />}
        />
      </Card>
      {deleting ? (
        <Modal
          title="Delete patient?"
          danger
          confirmLabel="Delete"
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await deletePatient(deleting.id, { uid: user.uid, email: user.email, displayName: profile.displayName }, `Patient: ${deleting.firstName} ${deleting.lastName}`);
            push("Patient deleted", "success");
            setDeleting(null);
            load();
          }}
        >
          <p>This cannot be undone.</p>
        </Modal>
      ) : null}
    </AppShell>
  );
}
